import React from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import { Button } from '@rocco/components/button';
import ShippingForm from './ShippingForm';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import type { ItemShippingType } from '../../context/ItemShippingContext';
import { useTracker } from '@archipro-website/tracker';

interface Props {
    open?: boolean;
    isSample: boolean;
    confirmButtonText?: string;
    loading?: boolean;
    onCancel?: () => void;
    onConfirm?: (shippingMethod: ItemShippingType | null) => void;
}

const ShippingPopup = ({
    open,
    onCancel,
    onConfirm,
    isSample,
    confirmButtonText = 'Confirm',
    loading,
}: Props) => {
    const { shipping, setShipping } = useVariantShipping(isSample);
    const [formBusy, setFormBusy] = React.useState(false);
    const [draftShipping, setDraftShipping] = React.useState(shipping);
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();

    React.useEffect(() => {
        setDraftShipping(shipping);
    }, [shipping]);

    const onConfirmClick = () => {
        setShipping(draftShipping);
        onConfirm?.(draftShipping);
    };

    const onClosing = () => {
        if (loading || formBusy) return;
        onCancel?.();
    };

    const title = `Select ${isSample ? 'sample ' : ''}shipping method`;

    const formContent = (
        <ShippingForm
            shipping={draftShipping}
            onChange={(s: ItemShippingType) => {
                tracker.log('ShippingOptionSelect', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                    data: { ExtraData: s.ID },
                });
                setDraftShipping?.(s);
            }}
            isSample={isSample}
            onFetchStatusChange={setFormBusy}
        />
    );

    if (mobile) {
        return (
            <Drawer
                open={open}
                onOpenChange={(isOpen) => !isOpen && onClosing()}
            >
                <DrawerContent scrollable limitMaxHeight>
                    <DrawerHeader variant="border" alignment="left">
                        <DrawerTitle className="text-base font-semibold">
                            {title}
                        </DrawerTitle>
                    </DrawerHeader>
                    <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-4">
                        {formContent}
                        <div className="mt-4 pt-4">
                            <Button
                                className="w-full"
                                onClick={onConfirmClick}
                                disabled={formBusy || loading}
                                isLoading={loading}
                            >
                                {confirmButtonText}
                            </Button>
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClosing()}>
            <DialogContent
                spacing="none"
                className="flex max-h-[85vh] max-w-[calc(100vw-2rem)] flex-col rounded-lg sm:max-w-[800px]"
            >
                <DialogHeader className="shrink-0 px-8 pb-2 pt-6">
                    <DialogTitle className="text-xl font-semibold">
                        {title}
                    </DialogTitle>
                </DialogHeader>
                <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 pt-4">
                    {formContent}
                </div>
                <DialogFooter className="shrink-0 border-t border-gray-100 px-8 py-5">
                    <Button
                        className="min-w-[160px]"
                        onClick={onConfirmClick}
                        disabled={formBusy || loading}
                        isLoading={loading}
                    >
                        {confirmButtonText}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShippingPopup;
